import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageLayout from "@/components/PageLayout";
import SEO from "@/components/SEO";
import FAQSchema from "@/components/FAQSchema";
import ContactModal from "@/components/new-landing/ContactModal";
import { trackEvent } from "@/utils/analytics";

const NAVY = "#002178";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const roles = [
  {
    number: "01",
    title: "CPO: The Strategic Anchor",
    description:
      "The CPO connects what the market needs, what technology enables, and what the business requires. They set product direction, define success metrics, and ensure every function is rowing in the same direction. Product is where strategy meets execution.",
  },
  {
    number: "02",
    title: "CTO: The Technical Foundation",
    description:
      "The CTO translates product vision into technical reality. They own architecture decisions, engineering team effectiveness, and the technical infrastructure that makes everything possible. The CPO says what to build; the CTO determines how.",
  },
  {
    number: "03",
    title: "CMO: The Market Voice",
    description:
      "The CMO ensures the market knows about the value the product delivers. They own positioning, demand generation, and brand. The CPO ensures the product delivers on what the CMO promises. When both are aligned, growth compounds.",
  },
  {
    number: "04",
    title: "COO: The Execution Engine",
    description:
      "The COO ensures the organization can deliver at scale. They own processes, operational efficiency, and cross-functional coordination. The CPO defines what success looks like; the COO ensures the machine can achieve it.",
  },
];

const faqs = [
  {
    question: "How do multiple fractional executives coordinate?",
    answer:
      "The same way full-time executives do: shared goals, clear accountability, and regular alignment. The difference is that fractional executives are typically more experienced and more efficient with their time. A well-structured fractional C-suite operates with clear swim lanes, shared OKRs, and weekly alignment touchpoints. The CPO often serves as the coordination point because product decisions affect every other function.",
  },
  {
    question:
      "Is a fractional C-suite model more cost-effective than full-time hires?",
    answer:
      "Significantly. A full fractional C-suite (CPO, CTO, CMO, COO) costs a fraction of what four full-time C-suite hires would cost in salary, equity, benefits, and recruiting fees. You get senior leadership calibrated to your current needs without the overhead of permanent headcount. As the company grows, you can transition any or all roles to full-time.",
  },
  {
    question: "Why should the CPO be the anchor of a fractional leadership team?",
    answer:
      "Product sits at the intersection of market needs, technical capability, and business outcomes. The CPO is uniquely positioned to connect what the CMO hears from the market, what the CTO can build, and what the COO can deliver. Without strong product leadership, the other functions optimize in isolation.",
  },
];

const HowFractionalExecutivesWorkTogether = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);

  return (
    <PageLayout>
      <SEO
        title="How Fractional Executives Work Together"
        description="Learn how fractional CPOs, CTOs, CMOs, and COOs coordinate as a leadership team. Discover why the CPO is the strategic anchor for technology companies."
        path="/how-fractional-executives-work-together"
      />

      {/* Hero */}
      <section className="py-24 md:py-40 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <motion.p
            {...fadeUp}
            className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-8 font-medium"
          >
            Fractional Leadership
          </motion.p>

          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-[clamp(2.5rem,8vw,6rem)] font-light leading-[0.95] tracking-[-0.03em] mb-12"
          >
            How Fractional Executives{" "}
            <span className="text-[#002178] font-medium">
              Work Together
            </span>
          </motion.h1>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-3xl"
          >
            <p className="text-xl md:text-2xl leading-relaxed font-medium text-[#1a1a1a] mb-6">
              Modern technology companies don't always need four full-time
              C-suite executives. They need the right leadership at the right
              time.
            </p>
            <p className="text-lg text-black/60 leading-relaxed">
              The fractional C-suite model gives companies access to senior
              leadership across product, technology, marketing, and
              operations without the cost and commitment of permanent hires.
              The key is coordination. Here's how these roles work together,
              and why the{" "}
              <Link
                to="/what-is-a-fractional-cpo"
                className="text-[#002178] underline underline-offset-4 hover:text-[#002178]/80"
              >
                CPO
              </Link>{" "}
              often serves as the anchor.
            </p>
          </motion.div>
        </div>
      </section>

      {/* The Fractional C-Suite Model */}
      <section
        className="py-24 md:py-32 px-6 md:px-12 lg:px-24"
        style={{ backgroundColor: NAVY }}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-start">
            <div className="md:sticky md:top-32">
              <motion.p
                {...fadeUp}
                className="text-sm uppercase tracking-[0.3em] text-white/50 mb-4 font-medium"
              >
                The Model
              </motion.p>
              <motion.h2
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] text-white mb-8"
              >
                The fractional{" "}
                <span className="font-medium">C-suite</span>
              </motion.h2>
              <motion.p
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-white/60 leading-relaxed"
              >
                Each role brings distinct expertise. Together, they form a
                coordinated leadership team with clear accountability, shared
                goals, and the experience to move fast.
              </motion.p>
            </div>

            <div className="space-y-0">
              {roles.map((role, i) => (
                <motion.div
                  key={i}
                  {...fadeUp}
                  transition={{ delay: i * 0.1 }}
                  className="group py-8 border-b border-white/10"
                >
                  <div className="flex gap-6 items-baseline">
                    <span className="text-white/20 font-mono text-sm">
                      {role.number}
                    </span>
                    <div>
                      <h3 className="text-xl md:text-2xl font-medium mb-2 text-white group-hover:text-white/80 transition-colors">
                        {role.title}
                      </h3>
                      <p className="text-white/50 leading-relaxed">
                        {role.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why the CPO is the Anchor */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-start">
            <div>
              <motion.h2
                {...fadeUp}
                className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8"
              >
                Why the CPO is{" "}
                <span className="text-[#002178] font-medium">the anchor</span>
              </motion.h2>
              <motion.div
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="space-y-4"
              >
                <p className="text-lg text-black/60 leading-relaxed">
                  Product is where strategy meets execution. The CPO sits at
                  the intersection of what the market needs, what technology
                  enables, and what the business requires.
                </p>
                <p className="text-lg text-black/60 leading-relaxed">
                  The CMO brings market intelligence. The CTO brings technical
                  capability. The COO brings operational capacity. The CPO
                  synthesizes all three into a product strategy that drives
                  growth.
                </p>
                <p className="text-lg text-black/60 leading-relaxed">
                  Without strong product leadership, the other functions
                  optimize in isolation. Marketing generates demand for the
                  wrong features. Engineering builds what's technically
                  interesting instead of what's commercially valuable.
                  Operations scales processes that don't need scaling.
                </p>
              </motion.div>
            </div>

            {/* How Roles Connect - Visual Description */}
            <div>
              <motion.h2
                {...fadeUp}
                className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8"
              >
                How the roles{" "}
                <span className="text-[#002178] font-medium">connect</span>
              </motion.h2>

              <motion.div
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="space-y-6"
              >
                {[
                  {
                    pair: "CPO + CMO",
                    connection:
                      "The CPO ensures the product delivers the value the CMO promises to the market. Together they align product capabilities with market positioning.",
                  },
                  {
                    pair: "CPO + CTO",
                    connection:
                      "The CPO defines outcomes; the CTO defines the technical path. Together they balance ambition with feasibility.",
                  },
                  {
                    pair: "CPO + COO",
                    connection:
                      "The CPO sets product direction; the COO ensures the organization can deliver. Together they connect strategy to operational capacity.",
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="border-l-4 border-[#002178] pl-6 py-2"
                  >
                    <p className="text-sm uppercase tracking-[0.2em] text-[#002178] font-medium mb-2">
                      {item.pair}
                    </p>
                    <p className="text-black/60 leading-relaxed">
                      {item.connection}
                    </p>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-24 md:py-32 px-6 md:px-12 lg:px-24"
        style={{ backgroundColor: NAVY }}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="max-w-3xl">
            <motion.h2
              {...fadeUp}
              className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] text-white mb-8"
            >
              Need senior product leadership{" "}
              <span className="font-medium">now?</span>
            </motion.h2>

            <motion.p
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-lg text-white/70 leading-relaxed mb-8"
            >
              Whether you need a standalone fractional CPO or a coordinated
              leadership team, we can help. Start with a{" "}
              <Link
                to="/assessment"
                className="text-white underline underline-offset-4 hover:text-white/90"
              >
                product assessment
              </Link>{" "}
              to identify your biggest growth levers.
            </motion.p>

            <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.2 }}>
              <button
                onClick={() => { trackEvent('contact_click', { source: 'cta_block' }); setIsContactOpen(true); }}
                className="inline-block px-8 py-4 bg-white text-[#002178] font-medium hover:scale-105 transition-transform"
              >
                Let's chat
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <div className="bg-[#F5F5F0]">
        <FAQSchema faqs={faqs} heading="Frequently Asked Questions" />
      </div>

      <ContactModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </PageLayout>
  );
};

export default HowFractionalExecutivesWorkTogether;
